import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useT } from "@magic-md/i18n/react";
import {
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardPaste,
  Copy,
  File,
  FilePlus,
  FolderMinus,
  FolderOpen as FolderOpenIcon,
  FolderPlus,
  Link,
  Minus,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Server,
  Star,
  Bookmark as BookmarkIcon,
  BookmarkX,
  Clock,
  History as HistoryIcon,
  Tag,
  Eye,
  Square,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { REVEAL_IN_FOLDER } from "../lib/external-openers";
import { buildExtensionContextMenuItems } from "../extensions/extension-menu-bridge";
import { buildFileNodeMenuContext } from "../extensions/virtual-package-menu-context";
import { useExtensionVirtualPackages } from "../extensions/useExtensionContributions";
import type {
  ExtensionVirtualPackageContext,
  ExtensionVirtualPackageNode,
  VirtualPackageContext,
} from "@magic-md/extension-sdk";
import { EXPLORER_REVEAL_EVENT } from "../lib/explorer-reveal";
import { fileNameFromPath } from "../lib/path-utils";
import {
  hasKnownDocumentPublishPreview,
  previewDocumentPublish,
} from "../lib/publish-preview";
import {
  getAssetRefFromPath,
  getAssetRelativePathFromPath,
  getFolderPathFromPublishDir,
  isDocumentAssetsPath,
  isFolderPublishDirPath,
  resolveFolderPublishOutDir,
} from "../lib/publish-utils";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { type FileTreeNode, useEditorStore } from "../store/editor-store";
import { type Bookmark, useBookmarkStore } from "../store/bookmark-store";
import { type SnapshotMeta, useHistoryStore } from "../store/history-store";
import {
  isActiveRunningPublishTask,
  type PublishTaskState,
  type SitePublishTaskState,
  usePublishStore,
} from "../store/publish-store";
import { type RecentItem, useRecentStore } from "../store/recent-store";
import { FileIcon } from "../theme/runtime/theme-icons";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { SidebarCloseButton } from "./SidebarCloseButton";
import { buildFilePathMenuItems } from "./context-menu-builders";
import { DocumentFileTreeNode } from "./DocumentFileTreeNode";
import { showFilePickerDialog } from "./dialogs";
import { showDocumentPublishDialog } from "./dialogs/DocumentPublishDialog";
import { showFolderPublishDialog } from "./dialogs/FolderPublishDialog";
import { OpenFilesSection } from "./OpenFilesSection";

/* ── DnD data types ── */
interface ExplorerDragData {
  type: "explorer-node";
  paths: string[];
  label: string;
  isDirectory: boolean;
}

function findExplorerNode(nodes: FileTreeNode[], path: string): FileTreeNode | null {
  for (const node of nodes) {
    if (node.path === path) return node;
    if (node.children) {
      const found = findExplorerNode(node.children, path);
      if (found) return found;
    }
  }
  return null;
}

function normalizeExplorerDragPaths(paths: string[], fileTree: FileTreeNode[]): string[] {
  const physicalPaths = Array.from(new Set(paths)).filter((path) => {
    const node = findExplorerNode(fileTree, path);
    return !!node && !node.isVirtual;
  });
  return physicalPaths.filter((path) =>
    !physicalPaths.some((parentPath) =>
      parentPath !== path && path.startsWith(`${parentPath}/`),
    ),
  );
}

type ExplorerRevealDetail = {
  path?: string;
};

const RECENT_FILES_LIMIT = 10;

function extensionVirtualPackagePath(
  extensionId: string,
  providerId: string,
  parentPath: string,
  nodeId: string,
): string {
  return `extension://${extensionId}/${providerId}/${encodeURIComponent(parentPath)}/${encodeURIComponent(nodeId)}`;
}

function buildExtensionPackageContext(node: FileTreeNode): ExtensionVirtualPackageContext {
  const virtual = buildFileNodeMenuContext(node).virtual;
  return {
    parentPath: node.path,
    parentName: node.name,
    parentKind: node.isWorkspaceRoot
      ? "workspace-root"
      : node.isDirectory
        ? "folder"
        : "file",
    bundlePath: node.bundlePath,
    virtual,
  };
}

function scrollExplorerNodeIntoView(path: string): void {
  if (typeof document === "undefined") return;
  const rows = document.querySelectorAll<HTMLElement>(
    "[data-explorer-node-path]",
  );
  for (const row of rows) {
    if (row.dataset.explorerNodePath !== path) continue;
    row.scrollIntoView?.({ block: "nearest" });
    break;
  }
}

/* ── File icon helper ── */
function getFileIcon(name: string, size: number) {
  return (
    <FileIcon
      name={name}
      size={size}
      className="shrink-0 text-muted-foreground"
      colorMode="currentColor"
    />
  );
}

function documentBundlePath(filePath: string): string | null {
  if (!/\.md$/i.test(filePath)) return null;
  return filePath.replace(/\.md$/i, ".magic-md");
}

function getDirectoryIcon(
  name: string,
  size: number,
  isOpen = false,
  isWorkspaceRoot = false,
) {
  return (
    <FileIcon
      name={name}
      size={size}
      isDirectory
      isOpen={isOpen}
      isWorkspaceRoot={isWorkspaceRoot}
      className="shrink-0 text-muted-foreground"
      colorMode="currentColor"
    />
  );
}

/* ── Inline name input (for rename / new file / new folder) ── */
function InlineInput({
  defaultValue,
  depth,
  icon,
  onCommit,
  onCancel,
}: {
  defaultValue: string;
  depth: number;
  icon: React.ReactNode;
  onCommit: (value: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const committingRef = useRef(false);

  useEffect(() => {
    // Focus and select the name (without extension for files)
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const dotIdx = defaultValue.lastIndexOf(".");
    if (dotIdx > 0) {
      el.setSelectionRange(0, dotIdx);
    } else {
      el.select();
    }
  }, [defaultValue]);

  const commitValue = (value: string) => {
    if (committingRef.current) return;
    if (!value) {
      onCancel();
      return;
    }
    committingRef.current = true;
    void Promise.resolve(onCommit(value)).catch(() => {
      committingRef.current = false;
    });
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = inputRef.current?.value.trim();
      commitValue(val ?? "");
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className="flex items-center w-full text-xs py-0.5 pr-2"
      style={{ paddingLeft: `${depth * 12 + 8 + 16}px` }}
    >
      <span className="mr-1.5 flex items-center shrink-0">{icon}</span>
      <input
        ref={inputRef}
        defaultValue={defaultValue}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (committingRef.current) return;
          const val = inputRef.current?.value.trim();
          if (val && val !== defaultValue) commitValue(val);
          else onCancel();
        }}
        className="flex-1 min-w-0 h-5 bg-background border border-primary/50 rounded px-1 text-xs font-mono outline-none focus:border-primary"
      />
    </div>
  );
}

function PublishStatusBadge({ task }: { task?: PublishTaskState }) {
  const t = useT("editor");
  if (!task) return null;
  if (task.status === "running" && !isActiveRunningPublishTask(task)) {
    return null;
  }

  const label =
    task.status === "running"
      ? task.progress?.percent != null
        ? t("explorer.publish.runningPercent", { percent: task.progress.percent })
        : t("explorer.publish.running")
      : task.status === "completed"
        ? t("explorer.publish.completed")
        : task.status === "failed"
          ? t("explorer.publish.failed")
          : t("explorer.publish.cancelled");

  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium",
        task.status === "running" && "bg-primary/10 text-primary",
        task.status === "completed" && "bg-emerald-500/10 text-emerald-600",
        task.status === "failed" && "bg-red-500/10 text-red-600",
        task.status === "cancelled" && "bg-muted text-muted-foreground",
      )}
    >
      {label}
    </span>
  );
}

function FolderPublishStatusBadge({ task }: { task?: SitePublishTaskState }) {
  const t = useT("editor");
  if (!task) return null;

  const label =
    task.status === "running"
      ? task.pagesTotal != null && task.pagesTotal > 0
        ? t("explorer.publish.runningPages", {
          processed: task.pagesProcessed ?? 0,
          total: task.pagesTotal,
        })
        : t("explorer.publish.running")
      : task.status === "completed"
        ? t("explorer.publish.completed")
        : task.status === "failed"
          ? t("explorer.publish.failed")
          : t("explorer.publish.cancelled");

  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium",
        task.status === "running" && "bg-primary/10 text-primary",
        task.status === "completed" && "bg-emerald-500/10 text-emerald-600",
        task.status === "failed" && "bg-red-500/10 text-red-600",
        task.status === "cancelled" && "bg-muted text-muted-foreground",
      )}
    >
      {label}
    </span>
  );
}

function PreviewServerStatusBadge({
  state,
}: {
  state: "starting" | "running" | null;
}) {
  const t = useT("editor");
  if (!state) return null;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium",
        state === "starting" && "bg-primary/10 text-primary",
        state === "running" && "bg-emerald-500/10 text-emerald-600",
      )}
    >
      <Server size={9} />
      {state === "starting" ? t("explorer.preview.starting") : t("explorer.preview.running")}
    </span>
  );
}

interface ExtensionVirtualPackageRowsProps {
  ownerContext: ExtensionVirtualPackageContext;
  packages: ReturnType<typeof useExtensionVirtualPackages>;
  depth: number;
  onSelect: (path: string, event: ReactMouseEvent) => boolean | void;
}

function buildExtensionPackageMenuContext(
  extensionId: string,
  providerId: string,
  node: ExtensionVirtualPackageNode,
  path: string,
  ownerContext: ExtensionVirtualPackageContext,
): VirtualPackageContext {
  return {
    isVirtual: true,
    kind: "extension",
    physicalPath: path,
    nodeName: node.name,
    isDirectory: !!node.isDirectory,
    packageName: providerId,
    ownerDocumentPath: ownerContext.virtual?.ownerDocumentPath,
    ownerBundlePath: ownerContext.virtual?.ownerBundlePath,
    ownerFolderPath:
      ownerContext.virtual?.ownerFolderPath ??
      (ownerContext.parentKind === "folder" ? ownerContext.parentPath : undefined),
    extensionId,
    extensionPackageId: providerId,
    extensionNodeId: node.id,
  };
}

function ExtensionVirtualPackageRows({
  ownerContext,
  packages,
  depth,
  onSelect,
}: ExtensionVirtualPackageRowsProps) {
  if (packages.length === 0) return null;
  return (
    <>
      {packages.map(({ extensionId, providerId, provider, node }) => {
        const path =
          node.path ??
          extensionVirtualPackagePath(extensionId, providerId, ownerContext.parentPath, node.id);
        return (
          <ExtensionVirtualPackageRow
            key={`${extensionId}:${providerId}:${node.id}:${path}`}
            extensionId={extensionId}
            providerId={providerId}
            providerIcon={provider.icon}
            node={node}
            path={path}
            ownerContext={ownerContext}
            depth={depth}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}

function ExtensionVirtualPackageRow({
  extensionId,
  providerId,
  providerIcon,
  node,
  path,
  ownerContext,
  depth,
  onSelect,
}: {
  extensionId: string;
  providerId: string;
  providerIcon?: ReactNode;
  node: ExtensionVirtualPackageNode;
  path: string;
  ownerContext: ExtensionVirtualPackageContext;
  depth: number;
  onSelect: (path: string, event: ReactMouseEvent) => boolean | void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ open: boolean; x: number; y: number }>({
    open: false,
    x: 0,
    y: 0,
  });
  const children = node.children ?? [];
  const isExpandable = !!node.isDirectory && children.length > 0;
  const virtual = buildExtensionPackageMenuContext(
    extensionId,
    providerId,
    node,
    path,
    ownerContext,
  );
  const contextItems = buildExtensionContextMenuItems({
    surface: "file",
    path,
    targetKind: node.isDirectory ? "folder" : "file",
    virtual,
  });

  const handleClick = (event: ReactMouseEvent) => {
    if (onSelect(path, event) === false) return;
    if (node.onOpen) {
      void node.onOpen(ownerContext);
      return;
    }
    if (isExpandable) setExpanded((value) => !value);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setCtxMenu({ open: true, x: event.clientX, y: event.clientY });
        }}
        data-explorer-node-path={path}
        className="flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors group opacity-60 text-foreground/80 hover:bg-muted/60"
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        title={node.title ?? path}
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">
          {isExpandable ? (
            expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
          ) : null}
        </span>
        <span className="mr-1.5 flex items-center text-muted-foreground">
          {node.icon ?? providerIcon ?? <Package size={14} />}
        </span>
        <span className="truncate">{node.name}</span>
      </button>
      {expanded && children.length > 0 && (
        <div>
          {children.map((child) => {
            const childPath = child.path ?? `${path}/${encodeURIComponent(child.id)}`;
            return (
              <ExtensionVirtualPackageRow
                key={`${extensionId}:${providerId}:${child.id}:${childPath}`}
                extensionId={extensionId}
                providerId={providerId}
                providerIcon={providerIcon}
                node={child}
                path={childPath}
                ownerContext={ownerContext}
                depth={depth + 1}
                onSelect={onSelect}
              />
            );
          })}
        </div>
      )}
      <ContextMenu
        open={ctxMenu.open}
        x={ctxMenu.x}
        y={ctxMenu.y}
        items={contextItems}
        onClose={() => setCtxMenu((state) => ({ ...state, open: false }))}
      />
    </>
  );
}

/* ── Tree Node ── */
function TreeNode({
  node,
  depth,
  renamingPath,
  setRenamingPath,
  creatingIn,
  setCreatingIn,
  selectedPaths,
  onSelect,
  dropTargetPath,
  setDropTargetPath,
}: {
  node: FileTreeNode;
  depth: number;
  renamingPath: string | null;
  setRenamingPath: (path: string | null) => void;
  creatingIn: { dirPath: string; type: "file" | "folder" } | null;
  setCreatingIn: (
    v: { dirPath: string; type: "file" | "folder" } | null,
  ) => void;
  selectedPaths: Set<string>;
  onSelect: (path: string, e: React.MouseEvent) => boolean | void;
  dropTargetPath: string | null;
  setDropTargetPath: (path: string | null) => void;
}) {
  const t = useT("editor");
  const toggleTreeNode = useEditorStore((s) => s.toggleTreeNode);
  const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
  const createFileInDir = useEditorStore((s) => s.createFileInDir);
  const createAssetFileInDir = useEditorStore((s) => s.createAssetFileInDir);
  const createAssetFolderInDir = useEditorStore((s) => s.createAssetFolderInDir);
  const createFolderInDir = useEditorStore((s) => s.createFolderInDir);
  const deleteNode = useEditorStore((s) => s.deleteNode);
  const renameNode = useEditorStore((s) => s.renameNode);
  const refreshSubtree = useEditorStore((s) => s.refreshSubtree);
  const expandTreeNode = useEditorStore((s) => s.expandTreeNode);
  const setStatusMessage = useEditorStore((s) => s.setStatusMessage);
  const setClipboardSource = useEditorStore((s) => s.setClipboardSource);
  const pasteInDir = useEditorStore((s) => s.pasteInDir);
  const hasClipboard = useEditorStore((s) => s._clipboardSource !== null);
  const removeWorkspaceFolder = useEditorStore((s) => s.removeWorkspaceFolder);
  const closeWorkspace = useEditorStore((s) => s.closeWorkspace);
  const workspaceFolderCount = useEditorStore((s) => s.workspaceFolders.length);
  const isStarred = useEditorStore((s) =>
    s.starred.some((x) => x.path === node.path),
  );
  const activeFilePath = useEditorStore(
    (s) => s.tabs.find((t) => t.id === s.activeTabId)?.filePath,
  );

  const isActive = !node.isDirectory && node.path === activeFilePath;
  const isSelected = selectedPaths.has(node.path);
  const isRenaming = renamingPath === node.path;
  const isDropTarget =
    dropTargetPath === node.path && node.isDirectory && !node.isVirtual;
  const baseExpandable = node.isDirectory || !!node.bundlePath;
  const extensionPackageContext = useMemo(
    () => buildExtensionPackageContext(node),
    [
      node.path,
      node.name,
      node.isDirectory,
      node.isWorkspaceRoot,
      node.bundlePath,
      node.isVirtual,
    ],
  );
  const extensionPackages = useExtensionVirtualPackages(extensionPackageContext);
  const hasExtensionPackages = extensionPackages.length > 0;
  const isExpandable = baseExpandable || hasExtensionPackages;

  /* ── History (snapshot) state ──
   * Two entry points share the same store:
   *   1. .md files — right-click → "创建快照"
   *   2. Virtual `<stem>.magic-md/history/` package node — expand to see snapshot tree
   */
  const isAssetsTreeNode = !!node.isVirtual && isDocumentAssetsPath(node.path);
  const isMarkdownFile = !node.isDirectory && /\.md$/i.test(node.name);
  const isMdFile = isMarkdownFile && !node.isVirtual;
  const shouldUseDocumentFileNode = isMarkdownFile && (!node.isVirtual || isAssetsTreeNode);
  const isHistoryBundleNode =
    !!node.isVirtual &&
    !!node.isDirectory &&
    /\.magic-md\/history$/.test(node.path);
  const isPublishBundleNode =
    !!node.isVirtual &&
    !!node.isDirectory &&
    /\/[^/]+\.magic-md\/publish$/.test(node.path);
  const isFolderPublishBundleNode =
    !!node.isVirtual &&
    !!node.isDirectory &&
    isFolderPublishDirPath(node.path);
  const assetRef = !node.isDirectory ? getAssetRefFromPath(node.path) : null;
  const assetRelativePath = getAssetRelativePathFromPath(node.path);
  const historyTargetPath = isMdFile
    ? node.path
    : isHistoryBundleNode
      ? node.path.replace(/\.magic-md\/history$/, ".md")
      : null;
  const publishTargetPath = isMdFile
    ? node.path
    : isPublishBundleNode
      ? node.path.replace(/\.magic-md\/publish$/, ".md")
      : null;
  const folderPublishTargetPath = isFolderPublishBundleNode
    ? getFolderPathFromPublishDir(node.path)
    : null;
  const folderPublishPath =
    folderPublishTargetPath ??
    (node.isDirectory && !node.isVirtual ? node.path : null);
  const nodeTitle = node.isSymlink && node.targetPath
    ? `${node.path} -> ${node.targetPath}`
    : node.path;
  const fileHistory = useHistoryStore((s) =>
    historyTargetPath ? s.files[historyTargetPath] : undefined,
  );
  const snapshotCount = fileHistory?.snapshots.length ?? 0;
  const loadHistoryIfNeeded = useHistoryStore((s) => s.loadIfNeeded);
  const refreshHistory = useHistoryStore((s) => s.refresh);
  const takeSnapshot = useHistoryStore((s) => s.take);
  const removeSnapshot = useHistoryStore((s) => s.remove);
  const publishTask = usePublishStore((s) =>
    publishTargetPath ? s.fileTasks[publishTargetPath] : undefined,
  );
  const hasPublishPreview =
    !!publishTargetPath && hasKnownDocumentPublishPreview(publishTargetPath);
  const folderPublishTask = usePublishStore((s) =>
    folderPublishPath ? s.folderTasks[folderPublishPath] : undefined,
  );
  const siteServer = usePublishStore((s) => s.siteServer);
  const cancelFolderPublish = usePublishStore((s) => s.cancelFolderPublish);
  const startSiteServer = usePublishStore((s) => s.startSiteServer);
  const stopSiteServer = usePublishStore((s) => s.stopSiteServer);
  const folderPublishOutputDir = folderPublishTargetPath
    ? (folderPublishTask?.result?.outputDir ??
      resolveFolderPublishOutDir(folderPublishTargetPath))
    : null;
  const previewServerState =
    folderPublishOutputDir && siteServer?.outputDir === folderPublishOutputDir
      ? siteServer.starting
        ? "starting"
        : siteServer.running
          ? "running"
          : null
      : null;

  /* Collapsible section state inside history bundle node */
  const [snapshotsSectionOpen, setSnapshotsSectionOpen] = useState(true);
  const [filesSectionOpen, setFilesSectionOpen] = useState(false);
  const [sectionCtx, setSectionCtx] = useState<{
    open: boolean;
    x: number;
    y: number;
    section: "snapshots" | "files" | null;
  }>({ open: false, x: 0, y: 0, section: null });
  const [deletingSnapshot, setDeletingSnapshot] = useState<string | null>(null);

  /* Lazy-load history when the virtual `history/` node expands */
  useEffect(() => {
    if (isHistoryBundleNode && node.expanded && historyTargetPath) {
      loadHistoryIfNeeded(historyTargetPath).catch(() => {});
    }
  }, [
    isHistoryBundleNode,
    node.expanded,
    historyTargetPath,
    loadHistoryIfNeeded,
  ]);

  /* ── @dnd-kit: draggable (disabled for virtual nodes) ── */
  const dragPaths = normalizeExplorerDragPaths(
    selectedPaths.has(node.path) ? Array.from(selectedPaths) : [node.path],
    useEditorStore.getState().fileTree,
  );
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `drag-${node.path}`,
    disabled: !!node.isVirtual,
    data: {
      type: "explorer-node",
      paths: dragPaths,
      label: node.name,
      isDirectory: node.isDirectory,
    } satisfies ExplorerDragData,
  });

  /* ── @dnd-kit: droppable (real directories only) ── */
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${node.path}`,
    disabled: !node.isDirectory || !!node.isVirtual,
  });

  // Combine refs for directories (both draggable and droppable)
  const combinedRef = useCallback(
    (el: HTMLButtonElement | null) => {
      setDragRef(el);
      if (node.isDirectory && !node.isVirtual) setDropRef(el);
    },
    [setDragRef, setDropRef, node.isDirectory, node.isVirtual],
  );

  // Update drop target visual when hovered
  useEffect(() => {
    if (isOver && node.isDirectory && !node.isVirtual) {
      setDropTargetPath(node.path);
    }
  }, [isOver, node.isDirectory, node.isVirtual, node.path, setDropTargetPath]);

  /* ── Context menu state ── */
  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
  }>({
    open: false,
    x: 0,
    y: 0,
  });

  const closeCtx = useCallback(
    () => setCtxMenu((s) => ({ ...s, open: false })),
    [],
  );
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY });
  };
  const takeSnapshotForFile = useCallback(
    async (filePath: string) => {
      const editor = useEditorStore.getState();
      const openTab = editor.tabs.find((tab) => tab.filePath === filePath);
      editor.setStatusMessage(t("explorer.history.snapshotTaking"), undefined);
      const snapshot = await takeSnapshot({
        filePath,
        trigger: "manual",
        content: openTab?.content,
      });
      if (!snapshot) {
        editor.setStatusMessage(t("explorer.history.snapshotFailed"), 4000);
        return;
      }
      await refreshHistory(filePath);
      window.dispatchEvent(
        new CustomEvent("magic-md:bundle-refresh", {
          detail: {
            filePath,
            bundleDir: documentBundlePath(filePath) ?? undefined,
          },
        }),
      );
      editor.setStatusMessage(t("explorer.history.snapshotCreated"), 4000);
    },
    [refreshHistory, takeSnapshot],
  );

  const importAssetIntoDir = useCallback(async (dirPath: string) => {
    const sourcePath = await showFilePickerDialog();
    if (!sourcePath) return;
    const name = fileNameFromPath(sourcePath);
    try {
      await getPlatformService().engine.workspaceCopy({
        from: sourcePath,
        to: `${dirPath}/${name}`,
        autoRename: true,
      });
      await refreshSubtree(dirPath);
      setStatusMessage(t("explorer.assets.imported", { name }), 2000);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("common.unknown");
      setStatusMessage(t("explorer.assets.importFailed", { message }), 4000);
    }
  }, [refreshSubtree, setStatusMessage]);

  const openInContainingFolder = useCallback(async (path: string) => {
    const platform = getPlatformService();
    if (!platform.openExternal) return;
    try {
      await platform.openExternal(REVEAL_IN_FOLDER.url(path));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setStatusMessage(t("menu.externalOpenFailed", {
        label: REVEAL_IN_FOLDER.label,
        message: message.slice(0, 80),
      }));
    }
  }, [setStatusMessage]);

  const handleClick = (e: React.MouseEvent) => {
    if (isRenaming) return;
    if (onSelect(node.path, e) === false) return;
    if (node.isVirtual) {
      // Virtual directory: toggle expand; virtual file: open in editor (paths are real on disk)
      if (node.isDirectory) toggleTreeNode(node.path);
      else openFileFromPath(node.path);
    } else if (node.isDirectory) {
      toggleTreeNode(node.path);
    } else if (node.bundlePath) {
      // Bundled .md file: open file only (chevron handles expansion)
      openFileFromPath(node.path);
    } else {
      openFileFromPath(node.path);
    }
  };

  /** Separate chevron click for bundled .md files */
  const handleChevronClick = (e: React.MouseEvent) => {
    if (node.bundlePath && !node.isDirectory) {
      e.stopPropagation();
      toggleTreeNode(node.path);
    }
  };

  /* Determine the target directory for "new file" / "new folder" / "paste" */
  const targetDir = node.isDirectory
    ? node.path
    : node.path.substring(0, node.path.lastIndexOf("/"));

  /* Build context menu items */
  const contextItems: ContextMenuItem[] = [];

  if (node.isVirtual) {
    if (isAssetsTreeNode) {
      if (node.isDirectory) {
        contextItems.push({
          key: "assets-new-file",
          label: t("explorer.assets.newFile"),
          icon: <FilePlus size={14} />,
          onClick: () => {
            void expandTreeNode(node.path);
            setCreatingIn({ dirPath: node.path, type: "file" });
          },
        });
        contextItems.push({
          key: "assets-new-folder",
          label: t("explorer.assets.newFolder"),
          icon: <FolderPlus size={14} />,
          onClick: () => {
            void expandTreeNode(node.path);
            setCreatingIn({ dirPath: node.path, type: "folder" });
          },
        });
        contextItems.push({
          key: "assets-import",
          label: t("explorer.assets.importFile"),
          icon: <Upload size={14} />,
          onClick: () => {
            void importAssetIntoDir(node.path);
          },
        });
        contextItems.push({
          key: "assets-paste",
          label: t("explorer.assets.paste"),
          icon: <ClipboardPaste size={14} />,
          disabled: !hasClipboard,
          onClick: () => pasteInDir(node.path),
        });
        contextItems.push({
          key: "assets-refresh",
          label: t("common.refresh"),
          icon: <RefreshCw size={14} />,
          separator: true,
          onClick: () => {
            void refreshSubtree(node.path);
          },
        });
      }
      if (assetRelativePath && node.isDirectory) {
        contextItems.push({
          key: "assets-copy-prefix",
          label: t("explorer.assets.copyRefPrefix"),
          icon: <Link size={14} />,
          separator: true,
          onClick: () => {
            navigator.clipboard
              .writeText(`$ref{assets/${assetRelativePath}/}`)
              .catch(() => {});
          },
        });
      }
      if (assetRef) {
        contextItems.push({
          key: "assets-copy-ref",
          label: t("explorer.assets.copyRef"),
          icon: <Link size={14} />,
          separator: true,
          onClick: () => {
            navigator.clipboard.writeText(assetRef).catch(() => {});
          },
        });
        contextItems.push({
          key: "assets-copy-markdown",
          label: t("explorer.assets.copyMarkdown"),
          icon: <Clipboard size={14} />,
          onClick: () => {
            const isImage = /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(node.name);
            const markdown = isImage
              ? `![${node.name}](${assetRef})`
              : `[${node.name}](${assetRef})`;
            navigator.clipboard.writeText(markdown).catch(() => {});
          },
        });
      }
      if (isAssetsTreeNode) {
        contextItems.push({
          key: "assets-reveal-in-finder",
          label: REVEAL_IN_FOLDER.label,
          icon: <REVEAL_IN_FOLDER.icon size={14} />,
          separator: true,
          disabled: !getPlatformService().openExternal,
          onClick: () => {
            void openInContainingFolder(node.path);
          },
        });
        contextItems.push({
          key: "assets-rename",
          label: t("common.rename"),
          icon: <Pencil size={14} />,
          separator: true,
          onClick: () => setRenamingPath(node.path),
        });
        contextItems.push({
          key: "assets-delete",
          label: t("common.delete"),
          icon: <Trash2 size={14} />,
          onClick: async () => {
            const parentDir = node.path.substring(0, node.path.lastIndexOf("/"));
            const deleted = await deleteNode(node.path, node.isDirectory, node.bundlePath);
            if (deleted && parentDir) await refreshSubtree(parentDir);
          },
        });
      }
    }
    if (isFolderPublishBundleNode && folderPublishTargetPath) {
      const serverRunning = siteServer?.running === true;
      const serverStarting = siteServer?.starting === true;
      const serverMatchesOutput =
        serverRunning &&
        !!folderPublishOutputDir &&
        siteServer?.outputDir === folderPublishOutputDir;
      const isPublishing = folderPublishTask?.status === "running";

      contextItems.push({
        key: "folder-publish",
        label: t("menu.publishFolder"),
        icon: <Upload size={14} />,
        onClick: () => {
          void showFolderPublishDialog(folderPublishTargetPath);
        },
      });
      if (isPublishing) {
        contextItems.push({
          key: "folder-publish-cancel",
          label: t("publish.action.cancelPublish"),
          icon: <Square size={14} />,
          onClick: () => {
            void cancelFolderPublish(folderPublishTargetPath);
          },
        });
      }
      contextItems.push({
        key: "folder-publish-refresh",
        label: t("common.refresh"),
        icon: <RefreshCw size={14} />,
        onClick: () => {
          void refreshSubtree(node.path);
        },
      });
      contextItems.push({
        key: "folder-publish-start-server",
        label: serverStarting
          ? t("publish.serverStarting")
          : serverMatchesOutput
            ? t("explorer.preview.serverReady")
            : serverRunning
              ? t("explorer.preview.switchServerHere")
              : t("publish.action.startPreviewServer"),
        icon: <Server size={14} />,
        disabled: !folderPublishOutputDir || serverStarting || serverMatchesOutput,
        separator: true,
        onClick: () => {
          if (!folderPublishOutputDir) return;
          void startSiteServer(folderPublishOutputDir);
        },
      });
      contextItems.push({
        key: "folder-publish-preview",
        label: t("explorer.preview.openInBrowser"),
        icon: <Eye size={14} />,
        disabled: !serverMatchesOutput || !siteServer?.url,
        onClick: () => {
          if (siteServer?.url) {
            getPlatformService().openExternal?.(siteServer.url);
          }
        },
      });
      if (serverRunning) {
        contextItems.push({
          key: "folder-publish-stop-server",
          label: t("publish.action.stopPreviewServer"),
          icon: <Square size={14} />,
          onClick: () => {
            void stopSiteServer();
          },
        });
      }
      contextItems.push({
        key: "folder-publish-reveal-output",
        label: t("explorer.publish.revealOutput"),
        icon: <FolderOpenIcon size={14} />,
        disabled: !folderPublishOutputDir,
        onClick: () => {
          if (!folderPublishOutputDir) return;
          const platform = getPlatformService();
          if (platform.openExternal && REVEAL_IN_FOLDER.url) {
            void platform.openExternal(
              REVEAL_IN_FOLDER.url(folderPublishOutputDir),
            );
          }
        },
      });
      contextItems.push({
        key: "folder-publish-delete",
        label: t("explorer.publish.deleteOutput"),
        icon: <Trash2 size={14} />,
        separator: true,
        onClick: () => {
          void (async () => {
            const ok = window.confirm(
              t("explorer.publish.confirmDeleteFolderOutput", { name: node.name }),
            );
            if (!ok) return;
            try {
              await getPlatformService().engine.workspaceRemove({
                path: node.path,
                recursive: true,
              });
              await refreshSubtree(folderPublishTargetPath).catch(() => {});
              setStatusMessage(t("explorer.publish.outputDeleted"));
            } catch (error) {
              const message = error instanceof Error ? error.message : t("common.unknown");
              setStatusMessage(t("explorer.publish.outputDeleteFailed", { message }));
            }
          })();
        },
      });
    }
    if (isPublishBundleNode && publishTargetPath) {
      contextItems.push({
        key: "publish-file",
        label: t("publish.document"),
        icon: <Upload size={14} />,
        onClick: async () => {
          await showDocumentPublishDialog(publishTargetPath);
        },
      });
      if (hasPublishPreview) {
        contextItems.push({
          key: "preview-publish",
          label: t("menu.previewPublish"),
          icon: <Eye size={14} />,
          onClick: async () => {
            await previewDocumentPublish(publishTargetPath);
          },
        });
      }
    }
    contextItems.push(
      ...buildFilePathMenuItems({
        path: node.path,
        t,
        kind: node.isDirectory ? "folder" : "file",
        includeFileName: false,
        includeRevealInFolder: false,
        includeExternalOpeners: false,
        includeStar: false,
        includeExtensionActions: false,
      }),
    );
    contextItems.push(
      ...buildExtensionContextMenuItems(buildFileNodeMenuContext(node), {
        firstSeparator: contextItems.length > 0,
      }),
    );
  } else {
    if (node.isDirectory) {
      contextItems.push({
        key: "new-file",
        label: t("explorer.newFile"),
        icon: <FilePlus size={14} />,
        onClick: () => {
          if (!node.expanded) toggleTreeNode(node.path);
          setCreatingIn({ dirPath: node.path, type: "file" });
        },
      });
      contextItems.push({
        key: "new-folder",
        label: t("explorer.newFolder"),
        icon: <FolderPlus size={14} />,
        onClick: () => {
          if (!node.expanded) toggleTreeNode(node.path);
          setCreatingIn({ dirPath: node.path, type: "folder" });
        },
      });
    }

    contextItems.push({
      key: "copy",
      label: t("common.copy"),
      icon: <Copy size={14} />,
      shortcut: "⌘C",
      separator: node.isDirectory,
      onClick: () => setClipboardSource(node.path, node.isDirectory),
    });

    contextItems.push({
      key: "paste",
      label: t("common.paste"),
      icon: <ClipboardPaste size={14} />,
      shortcut: "⌘V",
      disabled: !hasClipboard,
      onClick: () => pasteInDir(targetDir),
    });

    contextItems.push(
      ...buildFilePathMenuItems({
        path: node.path,
        t,
        kind: node.isDirectory ? "folder" : "file",
        title: node.name,
        includeExternalOpeners: true,
        includeDocumentActions: true,
        starLabel: node.name,
        firstSeparator: true,
      }),
    );

    if (node.isDirectory) {
      contextItems.push({
        key: "publish-folder",
        label: t("menu.publishFolder"),
        icon: <Upload size={14} />,
        separator: true,
        onClick: async () => {
          await showFolderPublishDialog(node.path);
        },
      });
    }

    if (isMdFile) {
      contextItems.push({
        key: "history-snapshot",
        label: t("explorer.history.createSnapshot"),
        icon: <Clock size={14} />,
        separator: true,
        onClick: async () => {
          await takeSnapshotForFile(node.path);
        },
      });
    }

    if (isHistoryBundleNode && historyTargetPath) {
      contextItems.push({
        key: "history-snapshot",
        label: t("explorer.history.createSnapshot"),
        icon: <Clock size={14} />,
        separator: true,
        onClick: async () => {
          await takeSnapshotForFile(historyTargetPath);
        },
      });
      contextItems.push({
        key: "history-refresh",
        label: t("common.refresh"),
        icon: <HistoryIcon size={14} />,
        onClick: () => {
          refreshHistory(historyTargetPath).catch(() => {});
        },
      });
    }

    contextItems.push({
      key: "rename",
      label: t("common.rename"),
      icon: <Pencil size={14} />,
      separator: true,
      disabled: node.isWorkspaceRoot,
      onClick: () => setRenamingPath(node.path),
    });

    if (node.isWorkspaceRoot) {
      contextItems.push({
        key: "remove-from-workspace",
        label: workspaceFolderCount > 1
          ? t("explorer.workspace.removeFolder")
          : t("explorer.workspace.close"),
        icon: <Minus size={14} />,
        onClick: async () => {
          try {
            if (workspaceFolderCount > 1) {
              await removeWorkspaceFolder(node.path);
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.folderRemoved"));
            } else {
              await closeWorkspace();
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.closed"));
            }
          } catch (err) {
            const { setStatusMessage } = useEditorStore.getState();
            const errMsg = err instanceof Error ? err.message : t("common.unknown");
            setStatusMessage(t("explorer.operationFailed", { message: errMsg }));
          }
        },
      });
    } else {
      contextItems.push({
        key: "delete",
        label: t("common.delete"),
        icon: <Trash2 size={14} />,
        onClick: async () => {
          try {
            await deleteNode(node.path, node.isDirectory, node.bundlePath);
            const { setStatusMessage } = useEditorStore.getState();
            setStatusMessage(t("explorer.deleted", { name: node.name }));
          } catch (err) {
            const { setStatusMessage } = useEditorStore.getState();
            const errMsg = err instanceof Error ? err.message : t("common.unknown");
            setStatusMessage(t("explorer.deleteFailed", { message: errMsg }));
          }
        },
      });
    }
  } // end of !node.isVirtual context menu branch

  /* Inline rename */
  if (isRenaming) {
    return (
      <>
        <InlineInput
          defaultValue={node.name}
          depth={depth}
          icon={
            node.isDirectory
              ? getDirectoryIcon(
                  node.name,
                  14,
                  node.expanded,
                  node.isWorkspaceRoot,
                )
              : getFileIcon(node.name, 14)
          }
          onCommit={async (newName) => {
            setRenamingPath(null);
            if (newName !== node.name) {
              try {
                await renameNode(node.path, newName);
              } catch (err) {
                const { setStatusMessage } = useEditorStore.getState();
                const errMsg = err instanceof Error ? err.message : t("common.unknown");
                setStatusMessage(t("explorer.renameFailed", { message: errMsg }));
              }
            }
          }}
          onCancel={() => setRenamingPath(null)}
        />
        {/* Still show children if expandable and expanded */}
        {isExpandable && node.expanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                renamingPath={renamingPath}
                setRenamingPath={setRenamingPath}
                creatingIn={creatingIn}
                setCreatingIn={setCreatingIn}
                selectedPaths={selectedPaths}
                onSelect={onSelect}
                dropTargetPath={dropTargetPath}
                setDropTargetPath={setDropTargetPath}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  if (shouldUseDocumentFileNode) {
    return (
      <DocumentFileTreeNode
        filePath={node.path}
        name={node.name}
        depth={depth}
        bundlePath={node.bundlePath ?? null}
        initialBundleChildren={node.children}
        initialBundleLoaded={node.loaded}
        initialExpanded={node.expanded}
        persistBundleExpansion
        isActive={isActive}
        isSelected={isSelected}
        isStarred={isStarred && !node.isVirtual}
        rowClassName={cn(isDragging && "opacity-40")}
        buttonRef={combinedRef}
        buttonProps={{ ...listeners, ...attributes }}
        contextItems={contextItems}
        onSelect={onSelect}
        onOpenFile={openFileFromPath}
        extraChildren={
          hasExtensionPackages ? (
            <ExtensionVirtualPackageRows
              ownerContext={extensionPackageContext}
              packages={extensionPackages}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ) : undefined
        }
        trailing={
          publishTask ? (
            <span className="ml-auto flex items-center gap-1">
              <PublishStatusBadge task={publishTask} />
            </span>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <button
        type="button"
        ref={combinedRef}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        {...listeners}
        {...attributes}
        data-explorer-node-path={node.path}
        aria-pressed={isSelected}
        className={cn(
          "flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors group",
          isDragging && "opacity-40",
          node.isVirtual && "opacity-60",
          isDropTarget
            ? "bg-primary/20 ring-1 ring-primary/40"
            : isSelected
              ? "bg-primary/15 text-primary"
              : isActive
                ? "bg-primary/10 text-primary"
                : "text-foreground/80 hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        title={nodeTitle}
      >
        {/* Expand/collapse chevron for expandable nodes */}
        {isExpandable ? (
          <span
            className="w-4 h-4 flex items-center justify-center shrink-0"
            role={node.bundlePath && !node.isDirectory ? "button" : undefined}
            onClick={
              node.bundlePath && !node.isDirectory
                ? handleChevronClick
                : undefined
            }
          >
            {node.expanded ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )}
          </span>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Icon */}
        <span className="mr-1.5 flex items-center">
          {isHistoryBundleNode ? (
            <HistoryIcon size={14} className="text-muted-foreground" />
          ) : node.isVirtual && node.isDirectory ? (
            <Package size={14} className="text-muted-foreground" />
          ) : node.isDirectory ? (
            getDirectoryIcon(node.name, 14, node.expanded, node.isWorkspaceRoot)
          ) : node.isVirtual ? (
            <File size={14} className="text-muted-foreground/60" />
          ) : (
            getFileIcon(node.name, 14)
          )}
        </span>

        {/* Name */}
        <span
          className={cn("truncate", node.isWorkspaceRoot && "font-semibold")}
        >
          {node.name}
        </span>
        {isStarred && !node.isVirtual && (
          <Star
            size={10}
            className="ml-1 shrink-0 text-amber-500 fill-amber-500"
          />
        )}
        {node.isSymlink && (
          <Link
            size={10}
            className="ml-1 shrink-0 text-muted-foreground/70"
          />
        )}
        {(publishTask ||
          folderPublishTask ||
          previewServerState ||
          (isHistoryBundleNode && fileHistory?.loaded)) && (
          <span className="ml-auto flex items-center gap-1">
            <PublishStatusBadge task={publishTask} />
            <FolderPublishStatusBadge task={folderPublishTask} />
            <PreviewServerStatusBadge state={previewServerState} />
            {isHistoryBundleNode && fileHistory?.loaded && (
              <span className="shrink-0 text-[10px] text-muted-foreground/60 font-mono">
                {snapshotCount}
              </span>
            )}
          </span>
        )}
      </button>

      {/* Children */}
      {isExpandable && node.expanded && node.children && (
        <div>
          {/* Snapshot timeline view (for history bundle nodes) */}
          {isHistoryBundleNode && historyTargetPath && (
            <>
              <button
                type="button"
                onClick={() => setSnapshotsSectionOpen((v) => !v)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSectionCtx({
                    open: true,
                    x: e.clientX,
                    y: e.clientY,
                    section: "snapshots",
                  });
                }}
                className="flex items-center w-full text-left text-[10px] uppercase tracking-wide text-muted-foreground/60 font-medium py-0.5 hover:text-foreground/80 hover:bg-muted/40 rounded"
                style={{ paddingLeft: `${(depth + 1) * 12 + 4}px` }}
              >
                <span className="mr-0.5 inline-flex items-center">
                  {snapshotsSectionOpen ? (
                    <ChevronDown size={10} />
                  ) : (
                    <ChevronRight size={10} />
                  )}
                </span>
                <span>{t("explorer.history.snapshots")}</span>
                {snapshotCount > 0 && (
                  <span className="ml-1.5 text-[9px] text-muted-foreground/50 font-mono normal-case tracking-normal">
                    {snapshotCount}
                  </span>
                )}
              </button>
              {snapshotsSectionOpen && (
                <SnapshotInlineList
                  filePath={historyTargetPath}
                  depth={depth + 2}
                  fileHistory={fileHistory}
                  deletingSnapshot={deletingSnapshot}
                  onRefresh={() => refreshHistory(historyTargetPath)}
                  onSnapshot={() =>
                    takeSnapshotForFile(historyTargetPath)
                  }
                  onRestore={() => {
                    // Restore is handled by the store directly
                    // The snapshot preview panel will display restore UI
                  }}
                  onDelete={async (snapshotId) => {
                    setDeletingSnapshot(snapshotId);
                    try {
                      await removeSnapshot(historyTargetPath, snapshotId);
                      const { setStatusMessage } = useEditorStore.getState();
                      setStatusMessage(t("explorer.history.snapshotDeleted"));
                    } catch {
                      const { setStatusMessage } = useEditorStore.getState();
                      setStatusMessage(t("explorer.history.snapshotDeleteFailed"));
                    } finally {
                      setDeletingSnapshot(null);
                    }
                  }}
                  onPreview={(snapshotId) => {
                    const { setSnapshotPreview } = useEditorStore.getState();
                    setSnapshotPreview(historyTargetPath, snapshotId);
                  }}
                />
              )}
              {node.children.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFilesSectionOpen((v) => !v)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSectionCtx({
                      open: true,
                      x: e.clientX,
                      y: e.clientY,
                      section: "files",
                    });
                  }}
                  className="flex items-center w-full text-left text-[10px] uppercase tracking-wide text-muted-foreground/60 font-medium py-0.5 mt-0.5 hover:text-foreground/80 hover:bg-muted/40 rounded"
                  style={{ paddingLeft: `${(depth + 1) * 12 + 4}px` }}
                >
                  <span className="mr-0.5 inline-flex items-center">
                    {filesSectionOpen ? (
                      <ChevronDown size={10} />
                    ) : (
                      <ChevronRight size={10} />
                    )}
                  </span>
                  <span>{t("explorer.files")}</span>
                  <span className="ml-1.5 text-[9px] text-muted-foreground/50 font-mono normal-case tracking-normal">
                    {node.children.length}
                  </span>
                </button>
              )}
            </>
          )}
          {/* Inline create new file/folder at the top of this directory (or assets virtual dirs) */}
          {(!node.isVirtual || isAssetsTreeNode) &&
            !node.bundlePath &&
            creatingIn?.dirPath === node.path && (
              <InlineInput
                defaultValue={
                  creatingIn.type === "file"
                    ? isAssetsTreeNode
                      ? "untitled.txt"
                      : "untitled.md"
                    : "new-folder"
                }
                depth={depth + 1}
                icon={
                  creatingIn.type === "file" ? (
                    <File size={14} className="text-muted-foreground" />
                  ) : (
                    getDirectoryIcon("folder", 14)
                  )
                }
                onCommit={(name) => {
                  void (async () => {
                    try {
                      if (creatingIn.type === "file") {
                        if (isAssetsTreeNode) {
                          await createAssetFileInDir(node.path, name);
                        } else {
                          await createFileInDir(node.path, name);
                        }
                      } else {
                        if (isAssetsTreeNode) {
                          await createAssetFolderInDir(node.path, name);
                        } else {
                          await createFolderInDir(node.path, name);
                        }
                      }
                    } catch (err) {
                      const { setStatusMessage } = useEditorStore.getState();
                      const errMsg =
                        err instanceof Error ? err.message : t("common.unknown");
                      setStatusMessage(t("explorer.createFailed", { message: errMsg }));
                    } finally {
                      setCreatingIn(null);
                    }
                  })();
                }}
                onCancel={() => setCreatingIn(null)}
              />
            )}
          {(!isHistoryBundleNode || filesSectionOpen) &&
            node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                depth={isHistoryBundleNode ? depth + 2 : depth + 1}
                renamingPath={renamingPath}
                setRenamingPath={setRenamingPath}
                creatingIn={creatingIn}
                setCreatingIn={setCreatingIn}
                selectedPaths={selectedPaths}
                onSelect={onSelect}
                dropTargetPath={dropTargetPath}
                setDropTargetPath={setDropTargetPath}
              />
            ))}
          {hasExtensionPackages && (
            <ExtensionVirtualPackageRows
              ownerContext={extensionPackageContext}
              packages={extensionPackages}
              depth={isHistoryBundleNode ? depth + 2 : depth + 1}
              onSelect={onSelect}
            />
          )}
          {node.children.length === 0 &&
            !hasExtensionPackages &&
            !creatingIn?.dirPath &&
            (!node.isVirtual || isAssetsTreeNode) &&
            !node.bundlePath && (
              <div
                className="text-[10px] text-muted-foreground italic py-1"
                style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
              >
                {t("explorer.emptyFolder")}
              </div>
            )}
        </div>
      )}

      {/* Context Menu */}
      <ContextMenu
        open={ctxMenu.open}
        x={ctxMenu.x}
        y={ctxMenu.y}
        items={contextItems}
        onClose={closeCtx}
      />

      {/* Section header context menu (history bundle: 快照 / 文件) */}
      {sectionCtx.section && historyTargetPath && (
        <ContextMenu
          open={sectionCtx.open}
          x={sectionCtx.x}
          y={sectionCtx.y}
          items={
            sectionCtx.section === "snapshots"
              ? [
                  {
                    key: "take",
                    label: t("explorer.history.createSnapshot"),
                    icon: <Clock size={14} />,
                    onClick: async () => {
                      await takeSnapshotForFile(historyTargetPath);
                      setSnapshotsSectionOpen(true);
                    },
                  },
                  {
                    key: "refresh",
                    label: t("common.refresh"),
                    icon: <HistoryIcon size={14} />,
                    onClick: () => {
                      refreshHistory(historyTargetPath).catch(() => {});
                    },
                  },
                  {
                    key: "toggle",
                    label: snapshotsSectionOpen ? t("common.collapse") : t("common.expand"),
                    icon: snapshotsSectionOpen ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    ),
                    separator: true,
                    onClick: () => setSnapshotsSectionOpen((v) => !v),
                  },
                ]
              : [
                  {
                    key: "refresh",
                    label: t("common.refresh"),
                    icon: <HistoryIcon size={14} />,
                    onClick: () => {
                      refreshHistory(historyTargetPath).catch(() => {});
                    },
                  },
                  {
                    key: "toggle",
                    label: filesSectionOpen ? t("common.collapse") : t("common.expand"),
                    icon: filesSectionOpen ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    ),
                    separator: true,
                    onClick: () => setFilesSectionOpen((v) => !v),
                  },
                ]
          }
          onClose={() =>
            setSectionCtx({ open: false, x: 0, y: 0, section: null })
          }
        />
      )}
    </>
  );
}

/* ── Inline snapshot list rendered under .md files in the file tree ── */
function SnapshotInlineList({
  filePath: _filePath,
  depth,
  fileHistory,
  deletingSnapshot,
  onRefresh: _onRefresh,
  onSnapshot,
  onRestore,
  onDelete,
  onPreview,
}: {
  filePath: string;
  depth: number;
  fileHistory:
    | ReturnType<typeof useHistoryStore.getState>["files"][string]
    | undefined;
  deletingSnapshot: string | null;
  onRefresh: () => void | Promise<unknown>;
  onSnapshot: () => void | Promise<unknown>;
  onRestore: (snapshotId: string) => void | Promise<unknown>;
  onDelete: (snapshotId: string) => void | Promise<unknown>;
  onPreview: (snapshotId: string) => void;
}) {
  const t = useT("editor");
  type GroupCtx = { kind: "file" };
  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    snapshot: SnapshotMeta | null;
    group: GroupCtx | null;
  }>({ open: false, x: 0, y: 0, snapshot: null, group: null });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    __file: true,
  });

  const closeCtx = () => setCtxMenu((s) => ({ ...s, open: false }));
  const baseStyle = { paddingLeft: `${depth * 12 + 8}px` };
  const childStyle = { paddingLeft: `${(depth + 1) * 12 + 8}px` };

  if (fileHistory?.loading && !fileHistory?.loaded) {
    return (
      <div
        className="text-[10px] text-muted-foreground italic py-1"
        style={baseStyle}
      >
        {t("common.loading")}
      </div>
    );
  }
  if (fileHistory?.error) {
    return (
      <div className="text-[10px] text-red-500 italic py-1" style={baseStyle}>
        {fileHistory.error}
      </div>
    );
  }

  const snapshots = fileHistory?.snapshots ?? [];

  if (snapshots.length === 0) {
    return null;
  }

  const ctxItemsForSnapshot = (s: SnapshotMeta): ContextMenuItem[] => [
    {
      key: "preview",
      label: t("common.preview"),
      icon: <Eye size={14} />,
      onClick: () => onPreview(s.id),
    },
    {
      key: "restore",
      label: t("explorer.history.restoreFull"),
      icon: <RefreshCw size={14} />,
      onClick: () => onRestore(s.id),
    },
    {
      key: "delete",
      label: deletingSnapshot === s.id
        ? t("explorer.history.deletingSnapshot")
        : t("explorer.history.deleteSnapshot"),
      icon: <Trash2 size={14} />,
      disabled: deletingSnapshot === s.id,
      onClick: () => onDelete(s.id),
    },
  ];

  const ctxItemsForGroup = (group: GroupCtx): ContextMenuItem[] => {
    void group;
    return [
      {
        key: "snapshot-doc",
        label: t("explorer.history.createFullSnapshot"),
        icon: <Clock size={14} />,
        onClick: () => onSnapshot(),
      },
    ];
  };

  const renderSnapshotRow = (
    s: SnapshotMeta,
    indent: { paddingLeft: string },
  ) => (
    <button
      key={s.id}
      type="button"
      onClick={() => onPreview(s.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setCtxMenu({
          open: true,
          x: e.clientX,
          y: e.clientY,
          snapshot: s,
          group: null,
        });
      }}
      className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground/80"
      style={indent}
      title={`${s.message ?? ""}\n${s.createdAt}`}
    >
      <span className="w-4 shrink-0" />
      <Clock size={10} className="mr-1.5 shrink-0 opacity-60" />
      <span className="truncate font-mono">{formatRelative(s.createdAt)}</span>
      <span className="ml-1.5 shrink-0 text-[9px] opacity-50">
        {triggerLabel(s.trigger)}
      </span>
      {s.tag && (
        <span className="ml-1 shrink-0 inline-flex items-center gap-0.5 px-1 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[9px]">
          <Tag size={8} />
          {s.tag}
        </span>
      )}
    </button>
  );

  const toggleGroup = (key: string) =>
    setOpenGroups((s) => ({ ...s, [key]: !(s[key] ?? false) }));

  const renderGroupRow = (
    groupKey: string,
    group: GroupCtx,
    label: string,
    icon: React.ReactNode,
    count: number,
    indent: { paddingLeft: string },
  ) => {
    const open = !!openGroups[groupKey];
    return (
      <button
        key={`hdr:${groupKey}`}
        type="button"
        onClick={() => toggleGroup(groupKey)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setCtxMenu({
            open: true,
            x: e.clientX,
            y: e.clientY,
            snapshot: null,
            group,
          });
        }}
        className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/60 text-foreground/80"
        style={indent}
      >
        <span className="mr-0.5 shrink-0 inline-flex items-center text-muted-foreground/60">
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </span>
        <span className="mr-1.5 shrink-0 inline-flex items-center text-muted-foreground/80">
          {icon}
        </span>
        <span className="truncate">{label}</span>
        <span className="ml-1.5 shrink-0 text-[9px] text-muted-foreground/60 font-mono">
          {count}
        </span>
      </button>
    );
  };

  return (
    <div>
      {/* File-level group */}
      {renderGroupRow(
        "__file",
        { kind: "file" },
        t("explorer.history.wholeFile"),
        <File size={11} />,
        snapshots.length,
        baseStyle,
      )}
      {openGroups.__file &&
        snapshots.map((s) => renderSnapshotRow(s, childStyle))}

      <ContextMenu
        open={ctxMenu.open}
        x={ctxMenu.x}
        y={ctxMenu.y}
        items={
          ctxMenu.snapshot
            ? ctxItemsForSnapshot(ctxMenu.snapshot)
            : ctxMenu.group
              ? ctxItemsForGroup(ctxMenu.group)
              : []
        }
        onClose={closeCtx}
      />
    </div>
  );
}

/* ── Starred section ── */
function StarredSection() {
  const t = useT("editor");
  const starred = useEditorStore((s) => s.starred);
  const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
  const [expanded, setExpanded] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    path: string;
  } | null>(null);

  if (starred.length === 0) return null;

  const ctxEntry = ctxMenu
    ? starred.find((item) => item.path === ctxMenu.path) ?? null
    : null;
  const ctxItems: ContextMenuItem[] = ctxEntry
    ? [
        ...(ctxEntry.kind === "file"
          ? [
              {
                key: "open",
                label: t("menu.openFile"),
                icon: <File size={14} />,
                onClick: () => openFileFromPath(ctxEntry.path),
              } satisfies ContextMenuItem,
            ]
          : []),
        ...buildFilePathMenuItems({
          path: ctxEntry.path,
          t,
          kind: ctxEntry.kind,
          title: ctxEntry.label || fileNameFromPath(ctxEntry.path),
          starLabel: ctxEntry.label,
          includeExternalOpeners: true,
          firstSeparator: ctxEntry.kind === "file",
        }),
      ]
    : [];

  return (
    <div className="border-b border-border/50 pb-1 mb-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex h-6 items-center w-full text-left text-xs px-2 rounded hover:bg-muted/60"
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <Star
          size={12}
          className="text-muted-foreground mr-1.5 shrink-0"
        />
        <span className="text-[11px] leading-none text-muted-foreground uppercase tracking-wider">
          Favorites ({starred.length})
        </span>
      </button>
      {expanded && (
        <div>
          {starred.map((s) =>
            s.kind === "file" && /\.md$/i.test(s.path) ? (
              <DocumentFileTreeNode
                key={s.path}
                filePath={s.path}
                name={fileNameFromPath(s.path)}
                label={s.label || fileNameFromPath(s.path)}
                depth={1}
                isStarred
                contextItems={[
                  {
                    key: "open",
                    label: t("menu.openFile"),
                    icon: <File size={14} />,
                    onClick: () => openFileFromPath(s.path),
                  },
                  ...buildFilePathMenuItems({
                    path: s.path,
                    t,
                    kind: "file",
                    title: s.label || fileNameFromPath(s.path),
                    starLabel: s.label,
                    includeExternalOpeners: true,
                    firstSeparator: true,
                  }),
                ]}
                onOpenFile={openFileFromPath}
              />
            ) : (
              <button
                key={s.path}
                type="button"
                onClick={() => {
                  if (s.kind === "file") openFileFromPath(s.path);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCtxMenu({
                    open: true,
                    x: e.clientX,
                    y: e.clientY,
                    path: s.path,
                  });
                }}
                className="flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors text-foreground/80 hover:bg-muted/60"
                style={{ paddingLeft: "20px" }}
                title={s.path}
              >
                <span className="mr-1.5 flex items-center">
                  {s.kind === "folder" ? (
                    <FolderOpenIcon size={14} className="text-muted-foreground" />
                  ) : (
                    getFileIcon(fileNameFromPath(s.path), 14)
                  )}
                </span>
                <span className="truncate">
                  {s.label || fileNameFromPath(s.path)}
                </span>
              </button>
            ),
          )}
        </div>
      )}
      {ctxMenu?.open && (
        <ContextMenu
          open={ctxMenu.open}
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxItems}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

/* ── Bookmarks section ── */
function BookmarksSection() {
  const t = useT("editor");
  const bookmarks = useBookmarkStore((s) => s.bookmarks);
  const removeBookmark = useBookmarkStore((s) => s.remove);
  const updateBookmark = useBookmarkStore((s) => s.update);
  const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
  const [expanded, setExpanded] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    id: string;
  } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  if (bookmarks.length === 0) return null;

  // Group by file path for readability
  const groups = new Map<string, Bookmark[]>();
  for (const b of bookmarks) {
    const list = groups.get(b.path) ?? [];
    list.push(b);
    groups.set(b.path, list);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => a.line - b.line);
  }

  const handleJump = async (path: string, line: number) => {
    await openFileFromPath(path);
    // Defer a tick for editor to mount, then dispatch goto-line
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("magic-md:goto-line", {
          detail: { line, flash: true },
        }),
      );
    }, 80);
  };

  return (
    <div className="pb-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex h-6 items-center w-full text-left text-xs px-2 rounded hover:bg-muted/60"
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <BookmarkIcon
          size={12}
          className="text-muted-foreground mr-1.5 shrink-0"
        />
        <span className="text-[11px] leading-none text-muted-foreground uppercase tracking-wider">
          Bookmarks ({bookmarks.length})
        </span>
      </button>
      {expanded && (
        <div>
          {Array.from(groups.entries()).map(([path, items]) => (
            <div key={path}>
              <div
                className="flex items-center text-[11px] text-muted-foreground py-0.5 truncate"
                style={{ paddingLeft: "20px" }}
                title={path}
              >
                {getFileIcon(fileNameFromPath(path), 12)}
                <span className="ml-1.5 truncate">
                  {fileNameFromPath(path)}
                </span>
              </div>
              {items.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center w-full text-xs py-0.5 pr-2 group hover:bg-muted/60 rounded"
                  style={{ paddingLeft: "36px" }}
                >
                  {renamingId === b.id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => {
                        void updateBookmark(b.id, {
                          label: renameValue.trim() || undefined,
                        });
                        setRenamingId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          void updateBookmark(b.id, {
                            label: renameValue.trim() || undefined,
                          });
                          setRenamingId(null);
                        } else if (e.key === "Escape") {
                          setRenamingId(null);
                        }
                      }}
                      className="flex-1 h-5 text-[11px] bg-background border border-border rounded px-1"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleJump(b.path, b.line)}
                      onDoubleClick={() => {
                        setRenameValue(b.label ?? "");
                        setRenamingId(b.id);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCtxMenu({
                          open: true,
                          x: e.clientX,
                          y: e.clientY,
                          id: b.id,
                        });
                      }}
                      className="flex-1 flex items-center text-left truncate"
                      title={b.label || t("explorer.bookmarks.lineTitle", { line: b.line })}
                    >
                      <span className="text-muted-foreground/70 text-[10px] tabular-nums w-8 shrink-0">
                        L{b.line}
                      </span>
                      <span className="truncate text-foreground/80">
                        {b.label || t("explorer.bookmarks.line", { line: b.line })}
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {ctxMenu?.open && (
        <ContextMenu
          open={ctxMenu.open}
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={[
            {
              key: "rename",
              label: t("explorer.bookmarks.renameLabel"),
              icon: <Pencil size={14} />,
              onClick: () => {
                const target = bookmarks.find(
                  (b: Bookmark) => b.id === ctxMenu.id,
                );
                if (target) {
                  setRenameValue(target.label ?? "");
                  setRenamingId(target.id);
                }
              },
            },
            {
              key: "remove",
              label: t("explorer.bookmarks.delete"),
              icon: <BookmarkX size={14} />,
              onClick: () => {
                void removeBookmark(ctxMenu.id);
              },
            },
          ]}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

function ExplorerTopSections() {
  const openTabCount = useEditorStore((s) => s.tabs.length);
  const recentFileCount = useRecentStore(
    (s) => s.items.filter((item) => item.type === "file").length,
  );

  if (openTabCount === 0 && recentFileCount === 0) {
    return null;
  }

  return (
    <div
      className="shrink-0 pt-1"
      data-explorer-section="top"
    >
      <RecentFilesSection />
      <OpenFilesSection embedded />
    </div>
  );
}

function ExplorerAuxiliarySections() {
  const starredCount = useEditorStore((s) => s.starred.length);
  const bookmarkCount = useBookmarkStore((s) => s.bookmarks.length);

  if (
    starredCount === 0 &&
    bookmarkCount === 0
  ) {
    return null;
  }

  return (
    <div
      className="shrink-0 overflow-auto border-t border-border/50 pt-1"
      style={{ maxHeight: "calc(100% - 160px)" }}
    >
      <StarredSection />
      <BookmarksSection />
    </div>
  );
}

/* ── History section ── */
function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(t).toLocaleDateString();
}

function triggerLabel(t: SnapshotMeta["trigger"]): string {
  switch (t) {
    case "manual":
      return "manual";
    case "on-save":
      return "on-save";
    case "auto-pre-restore":
      return "pre-restore";
    default:
      return t;
  }
}

/* ── Recent files section ── */
interface RecentFileTreeNode {
  key: string;
  name: string;
  path: string;
  isDirectory: boolean;
  filePath?: string;
  openedAt: number;
  children: RecentFileTreeNode[];
}

interface RecentWorkspaceRoot {
  path: string;
  name?: string | null;
}

function normalizeRecentPath(path: string): string {
  const normalized = path.replace(/\\/g, "/").replace(/\/+$/, "");
  return normalized || path;
}

function findRecentWorkspaceRoot(
  filePath: string,
  workspaceFolders: readonly RecentWorkspaceRoot[],
): RecentWorkspaceRoot | null {
  const normalizedFilePath = normalizeRecentPath(filePath);
  return workspaceFolders
    .filter((folder) => {
      const rootPath = normalizeRecentPath(folder.path);
      const boundary = rootPath === "/" ? "/" : `${rootPath}/`;
      return (
        normalizedFilePath === rootPath ||
        normalizedFilePath.startsWith(boundary)
      );
    })
    .sort(
      (a, b) =>
        normalizeRecentPath(b.path).length - normalizeRecentPath(a.path).length,
    )[0] ?? null;
}

function buildRecentPathSegments(
  filePath: string,
  workspaceFolders: readonly RecentWorkspaceRoot[],
): Array<{ name: string; path: string; isFile: boolean }> {
  const normalizedFilePath = normalizeRecentPath(filePath);
  const root = findRecentWorkspaceRoot(normalizedFilePath, workspaceFolders);

  if (root) {
    const rootPath = normalizeRecentPath(root.path);
    const rootName = root.name || fileNameFromPath(rootPath) || rootPath;
    const relativeParts = normalizedFilePath
      .slice(rootPath.length)
      .replace(/^\/+/, "")
      .split("/")
      .filter(Boolean);
    const segments: Array<{ name: string; path: string; isFile: boolean }> = [
      { name: rootName, path: rootPath, isFile: false },
    ];
    let currentPath = rootPath;
    relativeParts.forEach((part, index) => {
      currentPath = `${currentPath}/${part}`;
      segments.push({
        name: part,
        path: index === relativeParts.length - 1 ? filePath : currentPath,
        isFile: index === relativeParts.length - 1,
      });
    });
    return segments;
  }

  const parts = normalizedFilePath.split("/").filter(Boolean);
  if (parts.length === 0) {
    return [{ name: fileNameFromPath(filePath), path: filePath, isFile: true }];
  }

  const isAbsolute = normalizedFilePath.startsWith("/");
  let currentPath = "";
  return parts.map((part, index) => {
    currentPath = isAbsolute
      ? index === 0
        ? `/${part}`
        : `${currentPath}/${part}`
      : currentPath
        ? `${currentPath}/${part}`
        : part;
    return {
      name: part,
      path: index === parts.length - 1 ? filePath : currentPath,
      isFile: index === parts.length - 1,
    };
  });
}

function sortRecentTree(nodes: RecentFileTreeNode[]): RecentFileTreeNode[] {
  nodes.sort((a, b) => {
    const byTime = b.openedAt - a.openedAt;
    if (byTime !== 0) return byTime;
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  nodes.forEach((node) => sortRecentTree(node.children));
  return nodes;
}

function buildRecentFilesTree(
  items: readonly RecentItem[],
  workspaceFolders: readonly RecentWorkspaceRoot[],
): { nodes: RecentFileTreeNode[]; count: number } {
  const seen = new Set<string>();
  const files = [...items]
    .filter((item) => item.type === "file")
    .sort((a, b) => b.openedAt - a.openedAt)
    .filter((item) => {
      const key = normalizeRecentPath(item.path).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, RECENT_FILES_LIMIT);

  const roots: RecentFileTreeNode[] = [];

  for (const item of files) {
    const segments = buildRecentPathSegments(item.path, workspaceFolders);
    let siblings = roots;

    segments.forEach((segment) => {
      const nodeKey = `${segment.isFile ? "file" : "dir"}:${segment.path}`;
      let node = siblings.find((candidate) => candidate.key === nodeKey);
      if (!node) {
        node = {
          key: nodeKey,
          name: segment.name,
          path: segment.path,
          isDirectory: !segment.isFile,
          filePath: segment.isFile ? item.path : undefined,
          openedAt: item.openedAt,
          children: [],
        };
        siblings.push(node);
      } else if (item.openedAt > node.openedAt) {
        node.openedAt = item.openedAt;
      }
      siblings = node.children;
    });
  }

  return { nodes: sortRecentTree(roots), count: files.length };
}

function RecentFileTreeRow({
  node,
  depth,
  expandedDirs,
  onToggleDir,
  onOpenFile,
  activeFilePath,
}: {
  node: RecentFileTreeNode;
  depth: number;
  expandedDirs: Record<string, boolean>;
  onToggleDir: (path: string) => void;
  onOpenFile: (path: string) => void | Promise<void>;
  activeFilePath?: string;
}) {
  const t = useT("editor");
  const expanded = expandedDirs[node.path] ?? true;
  const isActive = !node.isDirectory && node.filePath === activeFilePath;
  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
  } | null>(null);

  const contextItems: ContextMenuItem[] =
    node.filePath
      ? [
          {
            key: "open",
            label: t("menu.openFile"),
            icon: <File size={14} />,
            onClick: () => {
              void onOpenFile(node.filePath!);
            },
          },
          ...buildFilePathMenuItems({
            path: node.filePath,
            t,
            kind: "file",
            title: node.name,
            includeExternalOpeners: true,
            includeDocumentActions: true,
            firstSeparator: true,
          }),
        ]
      : [];

  const handleClick = () => {
    if (node.isDirectory) {
      onToggleDir(node.path);
      return;
    }
    if (node.filePath) {
      void onOpenFile(node.filePath);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onContextMenu={(e) => {
          if (!node.filePath) return;
          e.preventDefault();
          e.stopPropagation();
          setCtxMenu({ open: true, x: e.clientX, y: e.clientY });
        }}
        data-recent-file-tree-node-path={node.path}
        data-recent-file-tree-node-kind={node.isDirectory ? "directory" : "file"}
        data-recent-file-tree-node-depth={depth}
        className={cn(
          "flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-foreground/80 hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${20 + depth * 12}px` }}
        title={node.filePath ?? node.path}
      >
        {node.isDirectory ? (
          <span className="w-4 h-4 flex items-center justify-center shrink-0">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className="mr-1.5 flex items-center shrink-0">
          {node.isDirectory
            ? getDirectoryIcon(node.name, 14, expanded)
            : getFileIcon(node.name, 14)}
        </span>
        <span className="truncate">{node.name}</span>
      </button>
      {node.isDirectory && expanded && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <RecentFileTreeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              expandedDirs={expandedDirs}
              onToggleDir={onToggleDir}
              onOpenFile={onOpenFile}
              activeFilePath={activeFilePath}
            />
          ))}
        </div>
      )}
      {ctxMenu?.open && node.filePath && (
        <ContextMenu
          open={ctxMenu.open}
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={contextItems}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </>
  );
}

function RecentFilesSection() {
  const t = useT("editor");
  const recentItems = useRecentStore((s) => s.items);
  const workspaceFolders = useEditorStore((s) => s.workspaceFolders);
  const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
  const activeFilePath = useEditorStore(
    (s) => s.tabs.find((tab) => tab.id === s.activeTabId)?.filePath,
  );
  const [expanded, setExpanded] = useState(false);
  const [expandedDirs, setExpandedDirs] = useState<Record<string, boolean>>({});

  const { nodes, count } = useMemo(
    () => buildRecentFilesTree(recentItems, workspaceFolders),
    [recentItems, workspaceFolders],
  );

  const toggleDir = useCallback((path: string) => {
    setExpandedDirs((state) => ({ ...state, [path]: !(state[path] ?? true) }));
  }, []);

  if (count === 0) return null;

  return (
    <div className="border-b border-border/50 pb-1 mb-1" data-explorer-section="recent-files">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex h-6 items-center w-full text-left text-xs px-2 rounded hover:bg-muted/60"
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <Clock
          size={12}
          className="text-muted-foreground mr-1.5 shrink-0"
        />
        <span className="text-[11px] leading-none text-muted-foreground uppercase tracking-wider">
          {t("recentFiles.titleWithCount", { count })}
        </span>
      </button>
      {expanded && (
        <div>
          {nodes.map((node) => (
            <RecentFileTreeRow
              key={node.key}
              node={node}
              depth={0}
              expandedDirs={expandedDirs}
              onToggleDir={toggleDir}
              onOpenFile={openFileFromPath}
              activeFilePath={activeFilePath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Explorer Panel ── */
export const ExplorerPanel = memo(function ExplorerPanel() {
  const t = useT("editor");
  const workspacePath = useEditorStore((s) => s.workspacePath);
  const workspaceFolders = useEditorStore((s) => s.workspaceFolders);
  const fileTree = useEditorStore((s) => s.fileTree);
  const openFolder = useEditorStore((s) => s.openFolder);
  const refreshFileTree = useEditorStore((s) => s.refreshFileTree);
  const moveNodes = useEditorStore((s) => s.moveNodes);
  const addWorkspaceFolder = useEditorStore((s) => s.addWorkspaceFolder);
  const saveWorkspace = useEditorStore((s) => s.saveWorkspace);
  const loadWorkspace = useEditorStore((s) => s.loadWorkspace);
  const closeWorkspace = useEditorStore((s) => s.closeWorkspace);

  /* Shared state for inline rename / create across the tree */
  const [renamingPath, setRenamingPath] = useState<string | null>(null);
  const [creatingIn, setCreatingIn] = useState<{
    dirPath: string;
    type: "file" | "folder";
  } | null>(null);

  /* Multi-selection state */
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const selectionAnchorPathRef = useRef<string | null>(null);
  const handleSelect = useCallback((path: string, e: React.MouseEvent) => {
    const additive = e.metaKey || e.ctrlKey;
    const range = e.shiftKey;
    setSelectedPaths((prev) => {
      if (range && selectionAnchorPathRef.current) {
        const visiblePaths: string[] = [];
        const collectVisiblePaths = (nodes: FileTreeNode[]) => {
          for (const node of nodes) {
            if (!node.isVirtual) visiblePaths.push(node.path);
            if (node.expanded && node.children) collectVisiblePaths(node.children);
          }
        };
        collectVisiblePaths(useEditorStore.getState().fileTree);
        const anchorIndex = visiblePaths.indexOf(selectionAnchorPathRef.current);
        const pathIndex = visiblePaths.indexOf(path);
        if (anchorIndex >= 0 && pathIndex >= 0) {
          const next = additive ? new Set(prev) : new Set<string>();
          const start = Math.min(anchorIndex, pathIndex);
          const end = Math.max(anchorIndex, pathIndex);
          for (let index = start; index <= end; index++) {
            const visiblePath = visiblePaths[index];
            if (visiblePath) next.add(visiblePath);
          }
          return next;
        }
      }
      if (additive) {
        // Toggle selection
        const next = new Set(prev);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        return next;
      }
      // Single selection
      return new Set([path]);
    });
    if (!range) selectionAnchorPathRef.current = path;
    return !(additive || range);
  }, []);

  useEffect(() => {
    const handleReveal = (event: Event) => {
      const detail = (event as CustomEvent<ExplorerRevealDetail>).detail;
      if (!detail?.path) return;
      setSelectedPaths(new Set([detail.path]));
      scrollExplorerNodeIntoView(detail.path);
    };
    window.addEventListener(EXPLORER_REVEAL_EVENT, handleReveal);
    return () => window.removeEventListener(EXPLORER_REVEAL_EVENT, handleReveal);
  }, []);

  /* Drag-and-drop state */
  const [dropTargetPath, setDropTargetPath] = useState<string | null>(null);
  const [dragOverlay, setDragOverlay] = useState<{
    label: string;
    count: number;
    isDirectory: boolean;
  } | null>(null);

  /* @dnd-kit sensors — PointerSensor for Tauri/WKWebView compatibility */
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as ExplorerDragData | undefined;
    if (data?.type === "explorer-node") {
      setDragOverlay({
        label: data.label,
        count: data.paths.length,
        isDirectory: data.isDirectory,
      });
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDragOverlay(null);
      setDropTargetPath(null);

      const dragData = event.active.data.current as
        | ExplorerDragData
        | undefined;
      if (!dragData || dragData.type !== "explorer-node") return;

      const overId = event.over?.id as string | undefined;
      if (!overId) return;

      // Determine target directory from droppable id
      let targetDir: string | null = null;
      if (overId === "drop-workspace-root") {
        targetDir = workspacePath ?? null;
      } else if (overId.startsWith("drop-")) {
        targetDir = overId.slice("drop-".length);
      }

      if (!targetDir) return;

      // Don't drop onto self or a child of the dragged items
      const paths = dragData.paths;
      if (paths.includes(targetDir)) return;
      if (paths.some((p) => targetDir!.startsWith(p + "/"))) return;

      void moveNodes(paths, targetDir).finally(() => {
        setSelectedPaths(new Set());
        selectionAnchorPathRef.current = null;
      });
    },
    [workspacePath, moveNodes],
  );

  const handleDragCancel = useCallback(() => {
    setDragOverlay(null);
    setDropTargetPath(null);
  }, []);

  /* Workspace root droppable */
  const { setNodeRef: setRootDropRef, isOver: isRootOver } = useDroppable({
    id: "drop-workspace-root",
  });

  // Sync root drop target visual
  useEffect(() => {
    if (isRootOver && workspacePath) {
      setDropTargetPath(workspacePath);
    }
  }, [isRootOver, workspacePath, setDropTargetPath]);

  /* Context menu for the empty area / background */
  const [bgCtxMenu, setBgCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
  }>({
    open: false,
    x: 0,
    y: 0,
  });

  const closeBgCtx = useCallback(
    () => setBgCtxMenu((s) => ({ ...s, open: false })),
    [],
  );

  const handleBgContextMenu = (e: React.MouseEvent) => {
    // Only trigger if clicking on the tree container background itself
    if (
      e.target === e.currentTarget ||
      (e.target as HTMLElement).closest("[data-tree-bg]")
    ) {
      e.preventDefault();
      setBgCtxMenu({ open: true, x: e.clientX, y: e.clientY });
    }
  };

  const bgContextItems: ContextMenuItem[] = workspacePath
    ? [
        {
          key: "new-file-root",
          label: t("explorer.newFile"),
          icon: <FilePlus size={14} />,
          onClick: () =>
            setCreatingIn({ dirPath: workspacePath, type: "file" }),
        },
        {
          key: "new-folder-root",
          label: t("explorer.newFolder"),
          icon: <FolderPlus size={14} />,
          onClick: () =>
            setCreatingIn({ dirPath: workspacePath, type: "folder" }),
        },
        {
          key: "open-folder",
          label: t("explorer.openAnotherFolder"),
          icon: <FolderOpenIcon size={14} />,
          separator: true,
          onClick: () => openFolder(),
        },
        {
          key: "add-folder",
          label: t("explorer.workspace.addFolder"),
          icon: <Plus size={14} />,
          onClick: async () => {
            try {
              const { showFolderPickerDialog } =
                await import("../components/dialogs");
              const dir = await showFolderPickerDialog({
                title: t("explorer.workspace.addFolder"),
              });
              if (dir) {
                addWorkspaceFolder(dir);
                const { setStatusMessage } = useEditorStore.getState();
                setStatusMessage(t("explorer.workspace.folderAdded"));
              }
            } catch {
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.addFolderFailed"));
            }
          },
        },
        {
          key: "save-workspace",
          label: t("explorer.workspace.save"),
          icon: <Save size={14} />,
          separator: true,
          onClick: async () => {
            try {
              await saveWorkspace();
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.saved"));
            } catch (err) {
              const { setStatusMessage } = useEditorStore.getState();
              const errMsg = err instanceof Error ? err.message : t("common.unknown");
              setStatusMessage(t("explorer.saveFailed", { message: errMsg }));
            }
          },
        },
        {
          key: "load-workspace",
          label: t("explorer.workspace.openFile"),
          icon: <Upload size={14} />,
          onClick: async () => {
            try {
              await loadWorkspace();
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.opened"));
            } catch (err) {
              const { setStatusMessage } = useEditorStore.getState();
              const errMsg = err instanceof Error ? err.message : t("common.unknown");
              setStatusMessage(t("dialog.openFailed", { message: errMsg }));
            }
          },
        },
        {
          key: "close-workspace",
          label: t("explorer.workspace.close"),
          icon: <FolderMinus size={14} />,
          onClick: async () => {
            try {
              await closeWorkspace();
              const { setStatusMessage } = useEditorStore.getState();
              setStatusMessage(t("explorer.workspace.closed"));
            } catch (err) {
              const { setStatusMessage } = useEditorStore.getState();
              const errMsg = err instanceof Error ? err.message : t("common.unknown");
              setStatusMessage(t("explorer.closeFailed", { message: errMsg }));
            }
          },
        },
        {
          key: "refresh",
          label: t("common.refresh"),
          icon: <RefreshCw size={14} />,
          separator: true,
          onClick: () => refreshFileTree(),
        },
      ]
    : [];

  if (!workspacePath) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            Explorer
          </span>
          <SidebarCloseButton />
        </div>
        <ExplorerTopSections />
        <div className="flex flex-1 min-h-0 flex-col">
          <div className="flex flex-1 min-h-[180px] flex-col items-center justify-center gap-3 overflow-auto px-4 py-1">
            <p className="text-xs text-muted-foreground text-center">
              {t("explorer.noFolderOpen")}
            </p>
            <button
              type="button"
              onClick={openFolder}
              className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t("file.openFolder")}
            </button>
            <button
              type="button"
              onClick={loadWorkspace}
              className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            >
              {t("command.workspace.open")}
            </button>
          </div>
        </div>
        <ExplorerAuxiliarySections />
      </div>
    );
  }

  const isMultiRoot = workspaceFolders.length > 1;
  const headerLabel = isMultiRoot ? t("explorer.workspace.title") : fileNameFromPath(workspacePath);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0"
        onContextMenu={handleBgContextMenu}
        title={t("explorer.moreActionsHint")}
      >
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider truncate flex-1">
          {headerLabel}
        </span>
        <SidebarCloseButton />
      </div>

      <ExplorerTopSections />

      {/* Tree */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          ref={setRootDropRef}
          role="tree"
          className={cn(
            "flex-1 min-h-[160px] overflow-auto py-1",
            dropTargetPath === workspacePath && "bg-primary/10",
          )}
          onContextMenu={handleBgContextMenu}
          data-tree-bg=""
        >
          {fileTree.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              renamingPath={renamingPath}
              setRenamingPath={setRenamingPath}
              creatingIn={creatingIn}
              setCreatingIn={setCreatingIn}
              selectedPaths={selectedPaths}
              onSelect={handleSelect}
              dropTargetPath={dropTargetPath}
              setDropTargetPath={setDropTargetPath}
            />
          ))}
          {fileTree.length === 0 && !creatingIn && (
            <div className="text-[10px] text-muted-foreground italic px-3 py-2">
              {t("explorer.emptyFolder")}
            </div>
          )}
        </div>

        {/* Drag overlay */}
        <DragOverlay dropAnimation={null}>
          {dragOverlay && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-popover border border-border shadow-lg text-xs pointer-events-none">
              {dragOverlay.isDirectory
                ? getDirectoryIcon(dragOverlay.label, 14)
                : getFileIcon(dragOverlay.label, 14)}
              <span className="truncate max-w-[160px]">
                {dragOverlay.label}
              </span>
              {dragOverlay.count > 1 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-medium leading-none">
                  {dragOverlay.count}
                </span>
              )}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {selectedPaths.size > 1 && (
        <div
          className="shrink-0 border-t border-border/70 px-3 py-1.5 text-[10px] text-muted-foreground"
          data-explorer-selection-summary=""
        >
          {t("explorer.selection.summary", { count: selectedPaths.size })}
        </div>
      )}

      <ExplorerAuxiliarySections />

      {/* Background context menu */}
      <ContextMenu
        open={bgCtxMenu.open}
        x={bgCtxMenu.x}
        y={bgCtxMenu.y}
        items={bgContextItems}
        onClose={closeBgCtx}
      />
    </div>
  );
});
